import { AG_GRID_LOCALE_DE, AG_GRID_LOCALE_EN } from '@ag-grid-community/locale';
import { Component, ElementRef, inject, OnDestroy, OnInit, Optional, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AgGridAngular, AngularFrameworkComponentWrapper, AngularFrameworkOverrides } from 'ag-grid-angular';
import { ColDef, DataTypeDefinition } from 'ag-grid-enterprise';
import { Subject, takeUntil } from 'rxjs';
import { DEFAULT_TABLE_ROWS } from 'src/environments/global.config';

import { CancomTableLoadingOverlay } from './loading-overlay/cancom-table-loading.component';

@Component({
	selector: 'cancom-table',
	template: '',
	styleUrls: ['./cancom-table.component.scss'],
	providers: [AngularFrameworkOverrides, AngularFrameworkComponentWrapper],
	encapsulation: ViewEncapsulation.None,
	standalone: false,
})
export class CancomTableComponent extends AgGridAngular implements OnInit, OnDestroy {
	private locales = {
		en: AG_GRID_LOCALE_EN,
		de: AG_GRID_LOCALE_DE,
	};

	// Add Custom Cell Data Types
	public dataTypeDefinitions: { [cellDataType: string]: DataTypeDefinition<any> } = {
		hyperLink: {
			extendsDataType: 'object',
			baseDataType: 'object',
			valueFormatter: (params) => (params.value == null ? '' : params.value.displayName),
			dataTypeMatcher: (value) => value && !!value.displayName && !!value.link,
		},
	};

	private destroy$ = new Subject<void>();
	private translate = inject(TranslateService);

	constructor(
		@Optional() elementRef: ElementRef,
		@Optional() viewContainerRef: ViewContainerRef,
		@Optional() angularFrameworkOverrides: AngularFrameworkOverrides,
		@Optional() angularFrameworkComponentWrapper: AngularFrameworkComponentWrapper
	) {
		super(elementRef, viewContainerRef, angularFrameworkOverrides, angularFrameworkComponentWrapper);

		// Set default theme and remove overwritten themes
		const classList = elementRef.nativeElement.classList as DOMTokenList;
		Array.from(classList).forEach((className) => {
			if (className.startsWith('ag-theme-')) {
				console.error('AgTheme provided. Please do not add any of the ag-theme styles.');
				classList.remove(className);
			}
		});
		classList.add('ag-theme-material');
	}

	ngOnInit(): void {
		const colDefPreset: ColDef = {
			sortable: true,
			filter: true,
			resizable: true,
			enableRowGroup: true,
			minWidth: 100,
			flex: 1,
			suppressHeaderFilterButton: true,
		};

		// Merge optional Component colDefs with our preset
		this.defaultColDef = { ...colDefPreset, ...this.defaultColDef };

		// Set default values
		this.paginationPageSize = DEFAULT_TABLE_ROWS;
		this.groupAllowUnbalanced = true;
		this.suppressMenuHide = false;
		this.loadingOverlayComponent = CancomTableLoadingOverlay;

		if (this.rowModelType === 'serverSide') {
			this.cacheBlockSize = DEFAULT_TABLE_ROWS;
		}

		// Set current language
		this.setLocale(this.translate.getCurrentLang() as keyof typeof this.locales);

		// Listen for language changes
		this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe((event) => {
			this.setLocale(event.lang as keyof typeof this.locales);
			// Recreate grid
			this.api?.destroy();
			this.ngAfterViewInit();
		});
	}

	private setLocale(locale: keyof typeof this.locales) {
		this.localeText = this.locales[locale] || this.locales.en;
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
